import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import {
  SafetyCertificateOutlined, KeyOutlined, ArrowLeftOutlined,
  CheckOutlined, CloseOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import { authHeaders } from "../utils/session";

/* ── Consts ──────────────────────────────────────────────────────────────── */
const PERMS = [
  { key: "can_view",      label: "View",    color: "#2563eb", bg: "#eff6ff", dot: "#3b82f6" },
  { key: "can_create",    label: "Add",     color: "#16a34a", bg: "#f0fdf4", dot: "#22c55e" },
  { key: "can_edit",      label: "Edit",    color: "#d97706", bg: "#fffbeb", dot: "#f59e0b" },
  { key: "can_delete",    label: "Delete",  color: "#dc2626", bg: "#fef2f2", dot: "#ef4444" },
  { key: "can_approve",   label: "Approve", color: "#0891b2", bg: "#ecfeff", dot: "#06b6d4" },
  { key: "import_export", label: "I/E",     color: "#7c3aed", bg: "#f5f3ff", dot: "#8b5cf6" },
];

const MODULE_COLORS = {
  leads: { bg: "#eff6ff", color: "#2563eb", dot: "#3b82f6" },
  dashboard: { bg: "#f0fdf4", color: "#16a34a", dot: "#22c55e" },
  content: { bg: "#fff7ed", color: "#d97706", dot: "#f59e0b" },
  orders: { bg: "#f5f3ff", color: "#7c3aed", dot: "#8b5cf6" },
  users: { bg: "#f0fdfa", color: "#0d9488", dot: "#14b8a6" },
  email: { bg: "#fdf2f8", color: "#db2777", dot: "#ec4899" },
};
const getModuleColor = (mod) => MODULE_COLORS[mod?.toLowerCase()] || { bg: "#f9fafb", color: "#6b7280", dot: "#9ca3af" };

const ROLE_COLORS = ["#5b48e8","#0284c7","#16a34a","#d97706","#dc2626","#7c3aed","#0891b2","#be185d"];
const roleColor = (name = "") => { let h = 0; for (const c of name) h = (h + c.charCodeAt(0)) % ROLE_COLORS.length; return ROLE_COLORS[h]; };
const authH = () => ({ ...authHeaders() });

const inputStyle = (err) => ({
  width: "100%", height: "44px", padding: "0 14px", borderRadius: "10px",
  border: `1.5px solid ${err ? "#ef4444" : "#e5e7eb"}`, fontSize: "14px",
  outline: "none", color: "#111827", boxSizing: "border-box",
  boxShadow: err ? "0 0 0 3px rgba(239,68,68,0.1)" : "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
});

const focusHandler = (e, err) => { if (!err) { e.target.style.borderColor = "#5b48e8"; e.target.style.boxShadow = "0 0 0 3px rgba(91,72,232,0.1)"; } };
const blurHandler = (e, err) => { if (!err) { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; } };

const PermChip = ({ perm, active, onChange }) => (
  <button type="button" onClick={onChange} style={{
    display: "inline-flex", alignItems: "center", gap: "4px",
    padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s",
    background: active ? perm.bg : "#f9fafb", color: active ? perm.color : "#9ca3af",
    border: `1px solid ${active ? perm.dot + "50" : "#e5e7eb"}`,
  }}>
    {active && <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: perm.dot, flexShrink: 0 }} />}
    {perm.label}
  </button>
);

/* ── Shield SVG Illustration ─────────────────────────────────────────────── */
const RoleIllustration = () => (
  <svg width="220" height="170" viewBox="0 0 220 170" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: "0 auto", display: "block" }}>
    {/* Ground shadow */}
    <ellipse cx="110" cy="158" rx="75" ry="10" fill="#e8ecff" opacity="0.5" />

    {/* Shield body */}
    <path d="M110 16 L170 42 L170 90 C170 120 145 145 110 155 C75 145 50 120 50 90 L50 42 Z" fill="url(#shieldGrad)" />
    <path d="M110 24 L162 46 L162 88 C162 114 140 137 110 147 C80 137 58 114 58 88 L58 46 Z" fill="white" opacity="0.12" />

    {/* Inner shield highlight */}
    <path d="M110 36 L150 55 L150 85 C150 108 133 126 110 134 C87 126 70 108 70 85 L70 55 Z" fill="white" opacity="0.08" />

    {/* Check mark */}
    <path d="M92 82 L104 94 L130 66" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Key icon (left) */}
    <g transform="translate(22, 55)">
      <circle cx="12" cy="12" r="10" fill="#d97706" opacity="0.8" />
      <circle cx="12" cy="10" r="5" fill="white" opacity="0.3" />
      <rect x="17" y="10" width="12" height="4" rx="2" fill="#d97706" opacity="0.8" />
      <rect x="25" y="10" width="4" height="8" rx="2" fill="#d97706" opacity="0.8" />
    </g>

    {/* Lock icon (right) */}
    <g transform="translate(172, 50)">
      <rect x="4" y="12" width="18" height="14" rx="3" fill="#0891b2" opacity="0.8" />
      <path d="M8 12 V8 C8 4 10 2 13 2 C16 2 18 4 18 8 V12" stroke="#0891b2" strokeWidth="2.5" fill="none" opacity="0.8" />
      <circle cx="13" cy="19" r="2" fill="white" opacity="0.4" />
    </g>

    {/* User badges */}
    <g transform="translate(30, 110)">
      <circle cx="10" cy="8" r="6" fill="#5b48e8" opacity="0.6" />
      <path d="M2 24 C2 17 5 14 10 14 C15 14 18 17 18 24" fill="#5b48e8" opacity="0.6" />
    </g>
    <g transform="translate(170, 105)">
      <circle cx="10" cy="8" r="6" fill="#16a34a" opacity="0.6" />
      <path d="M2 24 C2 17 5 14 10 14 C15 14 18 17 18 24" fill="#16a34a" opacity="0.6" />
    </g>

    {/* Connecting dots */}
    <line x1="48" y1="120" x2="62" y2="108" stroke="#5b48e8" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.25" />
    <line x1="172" y1="116" x2="158" y2="104" stroke="#16a34a" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.25" />

    {/* Sparkles */}
    <circle cx="38" cy="38" r="2.5" fill="#5b48e8" opacity="0.15" />
    <circle cx="182" cy="35" r="2" fill="#0891b2" opacity="0.2" />
    <circle cx="75" cy="18" r="1.5" fill="#d97706" opacity="0.25" />
    <circle cx="148" cy="14" r="2" fill="#16a34a" opacity="0.2" />
    <circle cx="20" cy="80" r="1.5" fill="#7c3aed" opacity="0.2" />
    <circle cx="200" cy="78" r="2" fill="#dc2626" opacity="0.15" />

    {/* Stars */}
    <path d="M195 22 L197 18 L199 22 L195 20 L199 20 Z" fill="#d97706" opacity="0.2" />
    <path d="M25 28 L27 24 L29 28 L25 26 L29 26 Z" fill="#5b48e8" opacity="0.2" />

    <defs>
      <linearGradient id="shieldGrad" x1="50" y1="16" x2="170" y2="155" gradientUnits="userSpaceOnUse">
        <stop stopColor="#5b48e8" />
        <stop offset="0.5" stopColor="#7c3aed" />
        <stop offset="1" stopColor="#764ba2" />
      </linearGradient>
    </defs>
  </svg>
);

/* ══════════════════════════════════════════════════════════════════════════════
   CreateEditRole — Full Page
══════════════════════════════════════════════════════════════════════════════ */
const CreateEditRole = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!editId;
  const isView = searchParams.get("view") === "1";

  const [saving, setSaving] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [roleName, setRoleName] = useState("");
  const [roleNameErr, setRoleNameErr] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [parentRoleIds, setParentRoleIds] = useState([]);
  const [roles, setRoles] = useState([]);

  const [permissionList, setPermissionList] = useState([]);
  const [selectedPermIds, setSelectedPermIds] = useState([]);

  const [menuList, setMenuList] = useState([]);
  const [selectedMenus, setSelectedMenus] = useState({});

  const initMenuState = useCallback((menus) => {
    const s = {};
    menus.forEach(m => {
      const p = {}; PERMS.forEach(x => { p[x.key] = 0; });
      s[m.id] = { selected: false, permissions: { ...p }, subMenus: {} };
      (m.sub_menus || []).forEach(sm => {
        const sp = {}; PERMS.forEach(x => { sp[x.key] = 0; });
        s[m.id].subMenus[sm.id] = { selected: false, permissions: { ...sp } };
      });
    });
    return s;
  }, []);

  useEffect(() => {
    const load = async () => {
      setPageLoading(true);
      try {
        const [rolesRes, menusRes, permsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/cms-roles`, { headers: authH() }),
          axios.get(`${API_BASE_URL}/cms-menus`, { headers: authH() }),
          axios.get(`${API_BASE_URL}/cms-permissions`, { headers: authH() }),
        ]);
        setRoles(rolesRes.data.data || []);
        setMenuList(menusRes.data.data || []);
        setPermissionList(permsRes.data.data || []);
        const menuState = initMenuState(menusRes.data.data || []);

        if (isEdit) {
          const detRes = await axios.get(`${API_BASE_URL}/cms-roles/${editId}`, { headers: authH() });
          const det = detRes.data.data;
          setRoleName(det?.role?.role_name || "");
          setRoleDescription(det?.role?.description || "");
          setParentRoleIds((det?.role?.reports_to || []).map(r => r.id));
          setSelectedPermIds((det?.permissions || []).map(p => p.id));
          (det?.menu_access || []).forEach(m => {
            if (!menuState[m.cms_menu_id]) return;
            menuState[m.cms_menu_id].selected = true;
            PERMS.forEach(p => { menuState[m.cms_menu_id].permissions[p.key] = m[p.key] ?? 0; });
            (m.sub_menus || []).forEach(sm => {
              if (!menuState[m.cms_menu_id].subMenus[sm.cms_sub_menu_id]) return;
              menuState[m.cms_menu_id].subMenus[sm.cms_sub_menu_id].selected = true;
              PERMS.forEach(p => { menuState[m.cms_menu_id].subMenus[sm.cms_sub_menu_id].permissions[p.key] = sm[p.key] ?? 0; });
            });
          });
        }
        setSelectedMenus(menuState);
      } catch { toast.error("Failed to load data"); }
      finally { setPageLoading(false); }
    };
    load();
  }, [editId, isEdit, initMenuState]);

  // Menu toggles
  const cloneMenus = () => JSON.parse(JSON.stringify(selectedMenus));
  const toggleMenu = (mid) => { if (isView) return; const n = cloneMenus(); n[mid].selected = !n[mid].selected; if (!n[mid].selected) Object.keys(n[mid].subMenus).forEach(sid => { n[mid].subMenus[sid].selected = false; }); setSelectedMenus(n); };
  const toggleSub = (mid, sid) => { if (isView) return; const n = cloneMenus(); n[mid].subMenus[sid].selected = !n[mid].subMenus[sid].selected; if (n[mid].subMenus[sid].selected) n[mid].selected = true; setSelectedMenus(n); };
  const togglePerm = (mid, key) => { if (isView) return; const n = cloneMenus(); n[mid].permissions[key] = n[mid].permissions[key] === 1 ? 0 : 1; setSelectedMenus(n); };
  const toggleSubPerm = (mid, sid, key) => { if (isView) return; const n = cloneMenus(); n[mid].subMenus[sid].permissions[key] = n[mid].subMenus[sid].permissions[key] === 1 ? 0 : 1; setSelectedMenus(n); };

  const selectAllMenus = () => { const n = cloneMenus(); Object.keys(n).forEach(mid => { n[mid].selected = true; PERMS.forEach(p => { n[mid].permissions[p.key] = 1; }); Object.keys(n[mid].subMenus).forEach(sid => { n[mid].subMenus[sid].selected = true; PERMS.forEach(p => { n[mid].subMenus[sid].permissions[p.key] = 1; }); }); }); setSelectedMenus(n); };
  const clearAllMenus = () => setSelectedMenus(initMenuState(menuList));

  const handleSubmit = async () => {
    if (!roleName.trim()) { setRoleNameErr("Role name is required"); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setRoleNameErr("");
    const menuAccess = [];
    Object.entries(selectedMenus).forEach(([mid, md]) => {
      if (!md.selected) return;
      const item = { cms_menu_id: parseInt(mid), permissions: md.permissions, sub_menus: [] };
      Object.entries(md.subMenus).forEach(([sid, sd]) => { if (sd.selected) item.sub_menus.push({ cms_sub_menu_id: parseInt(sid), permissions: sd.permissions }); });
      menuAccess.push(item);
    });
    setSaving(true);
    try {
      const payload = { role_name: roleName, description: roleDescription, permission_ids: selectedPermIds, menu_access: menuAccess, report_to_role_ids: parentRoleIds };
      if (isEdit) await axios.put(`${API_BASE_URL}/cms-roles/${editId}`, payload, { headers: { "Content-Type": "application/json", ...authH() } });
      else await axios.post(`${API_BASE_URL}/cms-roles`, payload, { headers: { "Content-Type": "application/json", ...authH() } });
      toast.success(isEdit ? "Role updated!" : "Role created!");
      navigate("/cms/manage-roles");
    } catch (err) { toast.error(err?.response?.data?.message || "Failed to save"); }
    finally { setSaving(false); }
  };

  // Derived
  const permsByModule = {};
  permissionList.forEach(p => { const mod = p.module || "general"; if (!permsByModule[mod]) permsByModule[mod] = []; permsByModule[mod].push(p); });
  const selectedMenuCount = Object.values(selectedMenus).filter(m => m.selected).length;
  const selectedSubCount = Object.values(selectedMenus).reduce((a, m) => a + Object.values(m.subMenus).filter(s => s.selected).length, 0);
  const totalPermsGranted = Object.values(selectedMenus).reduce((a, m) => { if (!m.selected) return a; return a + PERMS.filter(p => m.permissions[p.key] === 1).length + Object.values(m.subMenus).reduce((b, s) => b + (s.selected ? PERMS.filter(p => s.permissions[p.key] === 1).length : 0), 0); }, 0);

  if (pageLoading) {
    return (
      <div className="main-content"><div className="page-content"><div className="container-fluid">
        <div style={{ padding: "60px", textAlign: "center" }}>
          <div className="spinner-border" style={{ color: "#5b48e8", width: "40px", height: "40px" }} role="status" />
          <div style={{ fontSize: "14px", color: "#9ca3af", marginTop: "16px" }}>Loading role configuration...</div>
        </div>
      </div></div></div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Top Bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
            <div>
              <h4 style={{ margin: 0, fontSize: "22px", fontWeight: 800, color: "#111827" }}>{isView ? (roleName || "Role") : isEdit ? "Edit Role" : "Create Role"}</h4>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#9ca3af" }}>{isView ? `Role ID: ${editId}` : isEdit ? "Update role details, permissions and menu access" : "Define a new role with permissions and menu access"}</p>
            </div>
            <Link to="/cms/manage-roles" style={{ display: "flex", alignItems: "center", gap: "6px", padding: "9px 18px", borderRadius: "10px", border: "1px solid #e5e7eb", background: "white", color: "#374151", fontWeight: 600, fontSize: "13px", textDecoration: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <ArrowLeftOutlined /> Back to Roles
            </Link>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              SECTION 1: Role Details — Single compact card (full width)
          ═══════════════════════════════════════════════════════════════ */}
          <div style={{ background: "white", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 2px 12px rgba(0,0,0,.05)", overflow: "hidden", marginBottom: "24px" }}>
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: "10px", background: "#fafbfc" }}>
              <InfoCircleOutlined style={{ color: "#5b48e8", fontSize: "16px" }} />
              <span style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>Role Details</span>
            </div>
            <div style={{ padding: "24px" }}>
              {/* Row 1: Name, Key, Description */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.5fr", gap: "18px", marginBottom: "18px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "6px" }}>Role Name <span style={{ color: "#ef4444" }}>*</span></label>
                  <input value={roleName} onChange={e => { setRoleName(e.target.value); if (e.target.value.trim()) setRoleNameErr(""); }}
                    placeholder="e.g. Manager" style={{ ...inputStyle(roleNameErr), ...(isView ? { background: "#f9fafb", cursor: "default", color: "#374151" } : {}) }}
                    onFocus={e => !isView && focusHandler(e, roleNameErr)} onBlur={e => !isView && blurHandler(e, roleNameErr)} readOnly={isView} />
                  {roleNameErr && <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>{roleNameErr}</div>}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "6px" }}>Role Key <span style={{ fontSize: "11px", fontWeight: 400, color: "#9ca3af" }}>· Auto-generated</span></label>
                  <div style={{ padding: "11px 14px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "14px", color: "#6b7280", background: "#f9fafb", fontFamily: "monospace", height: "44px", boxSizing: "border-box", display: "flex", alignItems: "center" }}>
                    {roleName.trim() ? roleName.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") : <span style={{ color: "#d1d5db" }}>e.g. manager</span>}
                  </div>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "6px" }}>Description <span style={{ fontSize: "11px", fontWeight: 400, color: "#9ca3af" }}>optional</span></label>
                  <input value={roleDescription} onChange={e => setRoleDescription(e.target.value)}
                    placeholder="Brief description of this role's purpose" style={{ ...inputStyle(false), ...(isView ? { background: "#f9fafb", cursor: "default", color: "#374151" } : {}) }}
                    onFocus={e => !isView && focusHandler(e, false)} onBlur={e => !isView && blurHandler(e, false)} readOnly={isView} />
                </div>
              </div>
              {/* Row 2: Reports To */}
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "6px" }}>
                  Reports To (Parent Roles)
                  <span style={{ fontSize: "11px", fontWeight: 400, color: "#9ca3af", marginLeft: "6px" }}>Select which roles this role reports to. Leave empty for top-level.</span>
                  {parentRoleIds.length > 0 && <span style={{ fontSize: "11px", background: "#f0fdf4", color: "#16a34a", padding: "2px 8px", borderRadius: "10px", fontWeight: 700, marginLeft: "8px" }}>{parentRoleIds.length} selected</span>}
                </label>
                {roles.filter(r => r.id !== Number(editId)).length === 0 ? (
                  <div style={{ padding: "10px 14px", borderRadius: "10px", border: "1.5px dashed #e5e7eb", color: "#d1d5db", fontSize: "13px", textAlign: "center" }}>No other roles available yet. Create more roles first.</div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {roles.filter(r => r.id !== Number(editId)).map(r => {
                      const isSel = parentRoleIds.includes(r.id);
                      const c = roleColor(r.role_name);
                      return (
                        <button key={r.id} type="button" onClick={() => !isView && setParentRoleIds(prev => isSel ? prev.filter(id => id !== r.id) : [...prev, r.id])}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            padding: "8px 16px", borderRadius: "10px", fontSize: "13px", fontWeight: 600,
                            cursor: isView ? "default" : "pointer", transition: "all 0.15s",
                            background: isSel ? `${c}12` : "white",
                            color: isSel ? c : "#6b7280",
                            border: `1.5px solid ${isSel ? c : "#e5e7eb"}`,
                            boxShadow: isSel ? `0 2px 8px ${c}20` : "none",
                          }}>
                          <div style={{ width: "16px", height: "16px", borderRadius: "4px", flexShrink: 0, border: `2px solid ${isSel ? c : "#d1d5db"}`, background: isSel ? c : "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {isSel && <CheckOutlined style={{ color: "white", fontSize: "9px" }} />}
                          </div>
                          {r.role_name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              SECTION 2: Menu Permissions
          ═══════════════════════════════════════════════════════════════ */}
          
          <div style={{ background: "white", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 2px 12px rgba(0,0,0,.05)", overflow: "hidden", marginBottom: "24px" }}>

            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fafbfc" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <KeyOutlined style={{ color: "#5b48e8", fontSize: "18px" }} />
                <span style={{ fontSize: "17px", fontWeight: 700, color: "#111827" }}>Menu Permissions</span>
                <span style={{ fontSize: "12px", background: "#f5f3ff", color: "#5b48e8", padding: "2px 10px", borderRadius: "10px", fontWeight: 700 }}>{selectedMenuCount} menus</span>
              </div>
              {!isView && <div style={{ display: "flex", gap: "8px" }}>
                <button type="button" onClick={selectAllMenus} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 16px", borderRadius: "8px", border: "1.5px solid #16a34a", background: "white", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#f0fdf4"; }} onMouseLeave={e => { e.currentTarget.style.background = "white"; }}>
                  <CheckOutlined style={{ fontSize: "12px" }} /> Select All
                </button>
                <button type="button" onClick={clearAllMenus} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 16px", borderRadius: "8px", border: "1.5px solid #dc2626", background: "white", color: "#dc2626", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#fef2f2"; }} onMouseLeave={e => { e.currentTarget.style.background = "white"; }}>
                  <CloseOutlined style={{ fontSize: "12px" }} /> Clear All
                </button>
              </div>}
            </div>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: "16px" }}>
                {menuList.map(menu => {
                  const ms = selectedMenus[menu.id];
                  if (!ms) return null;
                  const hasSubs = menu.sub_menus?.length > 0;
                  return (
                    <div key={menu.id} style={{ border: "1.5px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", transition: "border-color 0.2s", ...(ms.selected ? { borderColor: "#5b48e8" } : {}) }}>
                      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: "10px", background: ms.selected ? "#f8f7ff" : "#fafafa", borderBottom: hasSubs && ms.selected ? "1px solid #e8ecff" : "none" }}>
                        <div onClick={() => toggleMenu(menu.id)} style={{ width: "20px", height: "20px", borderRadius: "5px", flexShrink: 0, border: `2px solid ${ms.selected ? "#5b48e8" : "#d1d5db"}`, background: ms.selected ? "#5b48e8" : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                          {ms.selected && <CheckOutlined style={{ color: "white", fontSize: "11px" }} />}
                        </div>
                        <span style={{ fontSize: "14px", fontWeight: 700, color: ms.selected ? "#111827" : "#6b7280", flex: 1 }}>{menu.menu_name}</span>
                        {hasSubs && <span style={{ fontSize: "11px", background: ms.selected ? "#5b48e8" : "#e5e7eb", color: ms.selected ? "white" : "#9ca3af", padding: "2px 8px", borderRadius: "10px", fontWeight: 700 }}>{menu.sub_menus.length}</span>}
                      </div>
                      {hasSubs && ms.selected && (
                        <div style={{ padding: "8px 12px 12px" }}>
                          {menu.sub_menus.map(sub => {
                            const ss = ms.subMenus[sub.id] || { selected: false, permissions: {} };
                            return (
                              <div key={sub.id} style={{ padding: "8px 10px", borderRadius: "8px", marginBottom: "4px", background: ss.selected ? "rgba(91,72,232,0.03)" : "transparent" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: ss.selected ? "6px" : 0 }}>
                                  <div onClick={() => toggleSub(menu.id, sub.id)} style={{ width: "16px", height: "16px", borderRadius: "4px", flexShrink: 0, border: `2px solid ${ss.selected ? "#5b48e8" : "#e5e7eb"}`, background: ss.selected ? "#5b48e8" : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                    {ss.selected && <CheckOutlined style={{ color: "white", fontSize: "9px" }} />}
                                  </div>
                                  <span style={{ fontSize: "13px", fontWeight: ss.selected ? 600 : 400, color: ss.selected ? "#374151" : "#9ca3af", flex: 1 }}>{sub.sub_menu_name}</span>
                                  {sub.sub_menu_link && <code style={{ fontSize: "10px", color: "#9ca3af", background: "#f3f4f6", padding: "1px 5px", borderRadius: "3px" }}>{sub.sub_menu_link}</code>}
                                </div>
                                {ss.selected && (
                                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", paddingLeft: "24px" }}>
                                    {PERMS.map(p => <PermChip key={p.key} perm={p} active={ss.permissions[p.key] === 1} onChange={() => toggleSubPerm(menu.id, sub.id, p.key)} />)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {!hasSubs && ms.selected && (
                        <div style={{ padding: "10px 16px", display: "flex", gap: "4px", flexWrap: "wrap" }}>
                          {PERMS.map(p => <PermChip key={p.key} perm={p} active={ms.permissions[p.key] === 1} onChange={() => togglePerm(menu.id, p.key)} />)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sticky Bottom */}
          {!isView && <div style={{ position: "sticky", bottom: 0, zIndex: 100, background: "white", borderTop: "2px solid #f0f0f0", padding: "16px 0", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", boxShadow: "0 -4px 20px rgba(0,0,0,0.06)" }}>
            <div style={{ flex: 1, fontSize: "12px", color: "#9ca3af" }}>
              {selectedPermIds.length} permissions · {selectedMenuCount} menus · {selectedSubCount} sub-menus · {parentRoleIds.length} parent role{parentRoleIds.length !== 1 ? "s" : ""}
            </div>
            <Link to="/cms/manage-roles" style={{ padding: "10px 24px", borderRadius: "10px", border: "1px solid #e5e7eb", background: "white", color: "#374151", fontWeight: 600, fontSize: "14px", textDecoration: "none" }}>Cancel</Link>
            <button onClick={handleSubmit} disabled={saving} style={{
              padding: "10px 32px", borderRadius: "10px", border: "none",
              background: saving ? "#a5b4fc" : "linear-gradient(135deg,#5b48e8,#7c3aed)",
              color: "white", fontWeight: 700, fontSize: "14px",
              cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "8px",
              boxShadow: "0 4px 14px rgba(91,72,232,0.35)",
            }}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" />}
              {saving ? "Saving..." : (isEdit ? "Update Role" : "Create Role")}
            </button>
          </div>}

        </div>
      </div>
    </div>
  );
};

export default CreateEditRole;